import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { apiServiceRTQ } from '@/apiError/apiShow';

export const useInfiniteSelect = ({
  url,
  params = {},
  pageSize = 20,
  searchParam = 'search',
  enabled = true,
  dedupeKey = 'referenceId',
  debounceMs = 400,
  nearBottomPx = 32,
}) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);
  const paramsKey = JSON.stringify(params);
  const baseKey = ['infiniteSelect', url, paramsKey, pageSize, searchParam];

  const query = useInfiniteQuery({
    queryKey: [...baseKey, search],
    queryFn: ({ pageParam }) =>
      apiServiceRTQ.get(url, {
        params: {
          ...params,
          pageNumber: pageParam,
          pageSize,
          ...(search ? { [searchParam]: search } : {}),
        },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.meta_data?.has_next ? allPages.length + 1 : undefined,
    enabled,
    retry: 0,
  });

  const { data, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
    query;

  const list = useMemo(() => {
    const seen = new Set();
    const rows = [];
    (data?.pages || []).forEach((page) => {
      (Array.isArray(page?.data) ? page.data : []).forEach((row) => {
        const key = row?.[dedupeKey];
        if (seen.has(key)) return;
        seen.add(key);
        rows.push(row);
      });
    });
    return rows;
  }, [data, dedupeKey]);

  const clearTimer = () => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
      searchTimer.current = null;
    }
  };

  useEffect(() => clearTimer, []);

  const loadMore = () => {
    if (hasNextPage && !isFetching) fetchNextPage();
  };

  const onPopupScroll = (e) => {
    const el = e.currentTarget || e.target;
    if (el.scrollTop + el.offsetHeight >= el.scrollHeight - nearBottomPx) {
      loadMore();
    }
  };

  const onSearch = (value) => {
    clearTimer();
    const next = (value || '').trim();
    searchTimer.current = setTimeout(() => {
      searchTimer.current = null;
      setSearch(next);
    }, debounceMs);
  };

  const onOpenChange = (open) => {
    if (open) return;
    clearTimer();
    if (search) setSearch('');
  };

  const reset = () => {
    clearTimer();
    setSearch('');
    queryClient.resetQueries({ queryKey: baseKey });
  };

  return {
    list,
    search,
    isFetching,
    isLoadingList: isFetching && !isFetchingNextPage,
    hasMore: !!hasNextPage,
    onPopupScroll,
    onSearch,
    onOpenChange,
    loadMore,
    reset,
  };
};

export default useInfiniteSelect;
